// Acente Temsilcisi'ne (subAgency) verilebilen sayfa/aksiyon yetkilerinin tek kaynağı.
// Form (Yetkiler alanı), route guard, sidebar ve buton gizleme buradan beslenir.
//
// Sayfa anahtarı → {paths, actions}
//  - paths: sayfanın yol önekleri. Detay sayfaları (/couriers/:id) en uzun önek eşleşmesiyle
//    üst sayfaya bağlanır.
//  - actions: "Görüntüleme" (view) dışındaki aksiyonlar. view her sayfada örtük vardır.
// Etiketler `pagePermissions` i18n namespace'indeki `pages.<sayfa>` ve `actions.<aksiyon>`
// anahtarlarından gelir. Yeni sayfa/aksiyon = buraya bir satır + i18n etiketi.
export const pagePermissions = {
    // "orderActions" = eski forceCourier + cancelOrder + splitOrder; bu üç düğme ayrı ayrı
    // gösterilmek yerine tek bir "Sipariş İşlemleri" yetkisinde birleştirildi
    // (bkz. ActiveOrdersTable.js).
    liveOperation: {
        paths: ["/anlik-operasyon"],
        actions: ["orderActions", "changeCourierStatus", "updateShift"]
    },
    // "orderActions" = eski assignCourier + cancelOrder + splitOrder + passOrder + updateOrder
    // (bkz. OrderTable.js, OrderDetail.js).
    orders: {
        paths: ["/orders"],
        actions: ["orderActions"]
    },
    // "createOrder" toplu sipariş yüklemeyi de kapsar (bkz. PickupInformation.js) — ayrı bir
    // "bulkImport" yetkisine gerek yok.
    createDeliveryRequest: {
        paths: ["/create-delivery-request"],
        actions: ["createOrder"]
    },
    // "excelActions" = eski import + export (şablon indirme de import'a bağlıydı, aynı
    // yetkiye taşındı); bkz. ShiftManagementActions.js.
    shiftManagement: {
        // Puantaj itirazları ayrı bir yolda olsa da vardiya yönetiminden açılır.
        paths: ["/shift-management", "/reports/shift-score"],
        actions: ["create", "excelActions", "updateShift", "shiftReport"]
    },
    // "excelActions" = eski export + import (bkz. Pools.js).
    pools: {
        paths: ["/pools"],
        actions: ["excelActions"]
    },
    poolIntensities: {paths: ["/pools/intensities"], actions: []},
    poolCourierShifts: {paths: ["/pools/courier-shifts"], actions: ["bookSlot"]},
    users: {paths: ["/users"], actions: ["edit"]},
    // "contracts" = Kurye detayındaki Sözleşmeler sekmesi: görüntüleme, sözleşme oluşturma ve
    // yükleme (bkz. CourierDetail.js). E-imzaya gönderme agency/subAgency'e kapalıdır.
    // "edit" sadece kurye temel bilgi düzenlemeyi kapsar (sayfada "Kurye Düzenleme" olarak
    // gösterilir, actionLabels). "statusHistory", "onboardingStatusHistory", "codSettings",
    // "walletView" ve "shifts" — önceden yalnızca role bağlı olan Durum Geçmişi/İşe Alım Durum
    // Geçmişi/Kapıda Ödeme (VigoPos)/Cüzdan/Vardiyalar sekmelerini artık ayrıca bu yetkilere de
    // bağlıyor (bkz. CourierDetail.js). "documentActions" = eski viewDocuments + approveDocuments
    // (bkz. CourierOnboardingTab.js, CourierDocuments.js). "callRecords" = Kurye Arama Kayıtları
    // sekmesi (bkz. CourierOnboardingTab.js).
    couriers: {
        paths: ["/couriers"],
        actions: [
            "edit",
            "statusHistory",
            "codSettings",
            "walletView",
            "onboardingStatusHistory",
            "shifts",
            "onboarding",
            "lifecycle",
            "documentActions",
            "callRecords",
            "equipment",
            "sendAnnouncement",
            "contracts",
            "tickets"
        ]
    },
    // "requestActions" = eski create + edit + close (bkz. CourierRequestsPage.js,
    // CourierRequestsTable.js).
    courierRequests: {
        paths: ["/couriers/courier-requests"],
        actions: ["requestActions", "export"]
    },
    payments: {
        // Acente (dolayısıyla acente temsilcisi) için ödeme/iptal/gider pusulası aksiyonları
        // isAgency kontrolüyle her zaman gizli (bkz. PaymentsPage/PaymentsTable), bu yüzden
        // yalnızca görüntüleme yetkisi verilebilir.
        paths: ["/payments"],
        actions: []
    },
    creditLogs: {paths: ["/credit-logs"], actions: ["createObjection", "export"]},
    subCompanyInvoices: {paths: ["/subcompany-invoices"], actions: []},
    packetObjection: {paths: ["/packet-objection"], actions: []},
    // "changeOnboardingStatus" kaldırıldı: onboarding durumu değiştirme agency rolüne
    // (dolayısıyla subAgency'e) kapalı (bkz. meStore "changeSubcompanyOnboardingStatus").
    // "users" = Alt Firma detayındaki Kullanıcılar sekmesi, önceden yalnızca role bağlıydı
    // (bkz. SubCompanyDetailWithTab.js).
    subcompanies: {
        paths: ["/subcompanies", "/companies/sub-company"],
        actions: ["export", "edit", "manageCredit", "manageIntegrations", "users", "tickets"]
    },
    subcompanyPricing: {
        paths: ["/subcompanies/pricing-list", "/sub-company/payment-contract"],
        actions: []
    },
    // "changeStatus", "changeOnboardingStatus" ve "codOptions" kaldırıldı: aktif/pasif etme,
    // onboarding durumu değiştirme ve kapıda ödeme ayarları agency rolüne (dolayısıyla
    // subAgency'e) kapalı (bkz. CompanyDetail.js, CompanyPage.js).
    companies: {
        paths: ["/companies"],
        actions: ["export", "orderMerge"]
    },
    // "/cash-on-delivery" (ana sayfa), "courier-terminals" ve "cash-on-delivery-summary"
    // agency rolüne kapatıldı ([VGT-418] Update agency permissions), bu yüzden registry'de yer
    // almıyor; yalnızca agency'ye açık kalan alt sekmeler (subCompanies, past-periods) burada.
    codSubcompanies: {paths: ["/cash-on-delivery/subCompanies"], actions: []},
    codPastPeriods: {paths: ["/cash-on-delivery/past-periods"], actions: ["export"]},
    walletCouriers: {paths: ["/wallet/couriers"], actions: ["export"]},
    // sendSms/createWithdraw butonları isAgency için her zaman gizli ([VGT-418]), export ise
    // (bkz. InactiveCourierWalletsTab) isAgency'den bağımsız gösteriliyor.
    walletInactiveCouriers: {
        paths: ["/wallet/inactive-couriers"],
        actions: ["export"]
    },
    // Talep Yönetimi sayfası; Kurye ve Alt Firma detaylarındaki "Talepler"
    // sekmeleri ilgili sayfanın "tickets" aksiyonuna bağlıdır.
    tickets: {paths: ["/ticket-management"], actions: []},
    // Acente başvurularını görme/düzenleme ayrıca dinamik yetki politikalarıyla (bkz.
    // constants/application) verilir; bu girdi yalnızca sayfaya girişi açar.
    applications: {paths: ["/applications"], actions: []},
    reports: {paths: ["/reports"], actions: ["download", "manageTemplates"]},
    shiftCompliance: {paths: ["/reports/shift-compliance"], actions: []}
};

// Yetki gerektirmeyen ortak yollar (registry dışında olsa da alt kullanıcıya açıktır).
const alwaysAllowedPaths = ["/profile"];

// "couriers" + "create" → "couriers.create"; backend'de auth.pagePermissions içinde saklanır.
export const permissionKey = (page, action) => `${page}.${action}`;

const normalizePath = path => path.replace(/\/+$/, "") || "/";
const isUnderPath = (pathname, path) => pathname === path || pathname.startsWith(`${path}/`);

// En uzun önek önce denenir: "/couriers/courier-requests", "/couriers"tan önce eşleşir.
const pathEntries = Object.entries(pagePermissions)
    .flatMap(([page, {paths}]) => paths.map(path => ({page, path})))
    .sort((a, b) => b.path.length - a.path.length);

const getPageKeyByPath = pathname => {
    const normalized = normalizePath(pathname);
    return pathEntries.find(({path}) => isUnderPath(normalized, path))?.page;
};

// Alt kullanıcı için yol erişimi: registry'de olup "view" yetkisi verilmiş ya da ortak yol olmalı.
export const isPathGranted = (pathname, grantedKeys = []) => {
    const normalized = normalizePath(pathname);
    if (alwaysAllowedPaths.some(path => isUnderPath(normalized, path))) {
        return true;
    }
    const page = getPageKeyByPath(normalized);
    return Boolean(page) && grantedKeys.includes(permissionKey(page, "view"));
};

// Alt kullanıcının girebileceği ilk sayfanın yolu; yönlendirme döngüsünü önlemek için kullanılır.
export const getFirstGrantedPath = (grantedKeys = []) =>
    Object.entries(pagePermissions).find(([page]) =>
        grantedKeys.includes(permissionKey(page, "view"))
    )?.[1].paths[0];
